// app/src/lib/cashflow/cashGapAlerts.js — cash-gap detection (US-3.5).
//
// Trigger: any forecast week whose closingCents falls below the configured
// threshold (default = 2× average weekly outflow) AND whose weekEnd is within
// the next 14 days from today.
//
// Alerts are deduped on (userId, kind=CASH_GAP, meta.weekStart).

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';
import { build as buildForecast } from './forecast.js';
import { get as getPrefs } from './prefs.js';
import { isoToday, daysBetween } from './util.js';

const HORIZON_DAYS = 14;

/**
 * Run the gap check. Returns the alerts emitted (existing alerts are upserted
 * so re-running doesn't pile up duplicates).
 */
export async function check({ adjustments } = {}) {
  const userId = await requireUserId();
  const today = isoToday();
  const prefs = await getPrefs();
  const forecast = await buildForecast({ adjustments });

  const threshold = prefs.gapThresholdCents != null
    ? prefs.gapThresholdCents
    : 2 * forecast.averageWeeklyOutflowCents;

  const emitted = [];
  for (const w of forecast.weeks) {
    if (daysBetween(w.weekEnd, today) > HORIZON_DAYS) continue;
    if (w.closingCents >= threshold) continue;
    const largestOutflow = w.items
      .filter((it) => it.kind === 'out')
      .sort((a, b) => b.amountCents - a.amountCents)[0];
    const title = largestOutflow
      ? `${formatMoney(largestOutflow.amountCents)} due ${humanDate(largestOutflow.dateIso)}. Projected balance ${formatMoney(w.closingCents)}.`
      : `Projected balance ${formatMoney(w.closingCents)} (< threshold) for week of ${w.weekStart}.`;

    const meta = {
      weekStart: w.weekStart,
      weekEnd: w.weekEnd,
      projectedClosingCents: w.closingCents,
      thresholdCents: threshold,
      largestOutflowCents: largestOutflow?.amountCents,
      largestOutflowLabel: largestOutflow?.label,
    };

    // Upsert: keep alertId stable for the same (kind, weekStart) pair so
    // refreshing the forecast doesn't spam new alerts.
    const existing = await db.alerts.where('userId').equals(userId)
      .filter((a) => a.kind === 'CASH_GAP' && a.meta?.weekStart === w.weekStart)
      .first();
    const row = {
      alertId: existing?.alertId || crypto.randomUUID(),
      userId,
      severity: 'critical',
      kind: 'CASH_GAP',
      title,
      createdAt: existing?.createdAt || Date.now(),
      dismissed: existing?.dismissed ?? 0,
      meta,
    };
    await db.alerts.put(row);
    emitted.push(row);
  }
  return emitted;
}

function formatMoney(c) { return `$${((c || 0) / 100).toFixed(2)}`; }
function humanDate(iso) { return new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }
